import { ApiService } from '../ApiService';
import type { ApiResponse } from '../types';
import type { Notification } from '../../../types/notifications';

export interface CreateNotificationRequest {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  link?: string;
  userId?: string; // Si non fourni, notification pour l'utilisateur actuel
}

export interface NotificationsListResponse {
  notifications: Notification[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  unreadCount: number;
}

export interface NotificationPreferences {
  email: {
    journal_validation: boolean;
    report_generation: boolean;
    user_mention: boolean;
    system_alerts: boolean;
    fiscal_year_closing: boolean;
  };
  browser: {
    journal_validation: boolean;
    report_generation: boolean;
    user_mention: boolean;
    system_alerts: boolean;
    fiscal_year_closing: boolean;
  };
}

export interface BulkNotificationAction {
  notificationIds: string[];
  action: 'read' | 'unread' | 'delete';
}

export const notificationsApi = {
  // Obtenir toutes les notifications de l'utilisateur
  getNotifications: (params?: {
    page?: number;
    pageSize?: number;
    unreadOnly?: boolean;
    type?: string;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<NotificationsListResponse>> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.pageSize) searchParams.append('pageSize', params.pageSize.toString());
    if (params?.unreadOnly) searchParams.append('unreadOnly', params.unreadOnly.toString());
    if (params?.type) searchParams.append('type', params.type);
    if (params?.from) searchParams.append('from', params.from);
    if (params?.to) searchParams.append('to', params.to);
    
    const query = searchParams.toString();
    return ApiService.get<NotificationsListResponse>(`/notifications${query ? `?${query}` : ''}`);
  },

  // Obtenir une notification par ID
  getNotificationById: (notificationId: string): Promise<ApiResponse<Notification>> =>
    ApiService.get<Notification>(`/notifications/${notificationId}`),

  // Créer une nouvelle notification
  createNotification: (notificationData: CreateNotificationRequest): Promise<ApiResponse<Notification>> =>
    ApiService.post<Notification>('/notifications', notificationData),

  // Marquer une notification comme lue
  markAsRead: (notificationId: string): Promise<ApiResponse<Notification>> =>
    ApiService.patch<Notification>(`/notifications/${notificationId}/read`, {}),

  // Marquer une notification comme non lue
  markAsUnread: (notificationId: string): Promise<ApiResponse<Notification>> =>
    ApiService.patch<Notification>(`/notifications/${notificationId}/unread`, {}),

  // Supprimer une notification
  deleteNotification: (notificationId: string): Promise<ApiResponse<void>> =>
    ApiService.delete(`/notifications/${notificationId}`),

  // Marquer toutes les notifications comme lues
  markAllAsRead: (): Promise<ApiResponse<{ updated: number }>> =>
    ApiService.post<{ updated: number }>('/notifications/mark-all-read', {}),

  // Supprimer toutes les notifications
  deleteAllNotifications: (): Promise<ApiResponse<{ deleted: number }>> =>
    ApiService.delete('/notifications/all'),

  // Actions groupées sur les notifications
  bulkAction: (action: BulkNotificationAction): Promise<ApiResponse<{ updated: number }>> =>
    ApiService.post<{ updated: number }>('/notifications/bulk-action', action),

  // Obtenir le nombre de notifications non lues
  getUnreadCount: (): Promise<ApiResponse<{ count: number }>> =>
    ApiService.get<{ count: number }>('/notifications/unread-count'),

  // Obtenir les préférences de notification de l'utilisateur
  getNotificationPreferences: (): Promise<ApiResponse<NotificationPreferences>> =>
    ApiService.get<NotificationPreferences>('/notifications/preferences'),

  // Mettre à jour les préférences de notification
  updateNotificationPreferences: (preferences: Partial<NotificationPreferences>): Promise<ApiResponse<NotificationPreferences>> =>
    ApiService.put<NotificationPreferences>('/notifications/preferences', preferences),

  // Obtenir les types de notifications disponibles
  getNotificationTypes: (): Promise<ApiResponse<{
    types: Array<{
      key: string;
      name: string;
      description: string;
      defaultEnabled: {
        email: boolean;
        browser: boolean;
      };
    }>;
  }>> =>
    ApiService.get('/notifications/types'),

  // Envoyer une notification de test
  sendTestNotification: (type: string): Promise<ApiResponse<Notification>> =>
    ApiService.post<Notification>('/notifications/test', { type })
};
